import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PageWrap, PageHeader, Card, Skeleton, useIsMobile } from '../components/UI';
import { useOnSyncComplete } from '../hooks/useSyncStatus';
import { api } from '../api';
import { AUDIT_STATUS_CONFIG, CA_AUDIT_STATUSES, TERMINAL_STATUSES } from '../auditStatus';
import '../styles/EngagementsDashboard.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function StatusBadge({ status }) {
  const c = AUDIT_STATUS_CONFIG[status] || AUDIT_STATUS_CONFIG.synced;
  return (
    <span
      className="eng-status-badge"
      style={{ background: c.bg, color: c.color, border: `1px solid ${c.color}33` }}
    >
      {c.label}
    </span>
  );
}

function SummaryCard({ label, count, color }) {
  return (
    <div className="eng-summary-card">
      <div className="eng-summary-label">{label}</div>
      <div className="eng-summary-count" style={{ color: color || 'var(--text)' }}>{count}</div>
    </div>
  );
}

function InlineStatusChanger({ eng, userRole, onStatusSaved }) {
  const isCAOrAdmin = userRole?.isCA || userRole?.isAdmin;
  const [open,   setOpen]   = useState(false);
  const [picked, setPicked] = useState('');
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false); setPicked(''); setErr('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!isCAOrAdmin) return null;

  const save = async (status) => {
    setSaving(true); setErr('');
    try {
      await api.updateEngagementStatus(
        eng.id, status,
        `Status set to "${AUDIT_STATUS_CONFIG[status]?.label}" from dashboard`,
      );
      onStatusSaved(eng.id, status);
      setOpen(false); setPicked(''); setErr('');
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={ref} className="eng-set-wrap" onClick={e => e.stopPropagation()}>
      <button
        className="eng-set-btn"
        onClick={() => { setOpen(o => !o); setPicked(''); setErr(''); }}
        title="Set engagement status"
      >
        ✏ Set
      </button>

      {open && (
        <div className="eng-status-popover">
          <div className="eng-popover-title">Set Status</div>
          <div className="eng-popover-options">
            {CA_AUDIT_STATUSES.map(s => {
              const c = AUDIT_STATUS_CONFIG[s];
              const active = picked === s;
              return (
                <button
                  key={s}
                  onClick={() => setPicked(active ? '' : s)}
                  className={`eng-popover-opt${active ? ' eng-popover-opt--active' : ''}`}
                  style={{
                    borderColor: active ? c.color : 'var(--border)',
                    background:  active ? c.bg    : 'transparent',
                    color:       active ? c.color : 'var(--text2)',
                    fontWeight:  active ? 600     : 400,
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          {err && <div className="eng-popover-err">{err}</div>}
          <div className="eng-popover-actions">
            <button
              className="eng-btn-cancel"
              onClick={() => { setOpen(false); setPicked(''); setErr(''); }}
            >
              Cancel
            </button>
            <button
              className="eng-btn-confirm"
              onClick={() => picked && save(picked)}
              disabled={!picked || saving}
              style={{
                background: picked ? AUDIT_STATUS_CONFIG[picked]?.color : 'var(--border)',
                opacity:    !picked || saving ? 0.5 : 1,
                cursor:     picked && !saving ? 'pointer' : 'not-allowed',
              }}
            >
              {saving ? 'Saving…' : 'Confirm'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function InlineDeleteButton({ eng, userRole, onDeleted }) {
  const isCAOrAdmin = userRole?.isCA || userRole?.isAdmin;
  const isTerminal  = TERMINAL_STATUSES.includes(eng.status);
  const [open,     setOpen]     = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err,      setErr]      = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false); setErr('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!isCAOrAdmin || isTerminal) return null;

  const handleDelete = async () => {
    setDeleting(true); setErr('');
    try {
      await api.deleteEngagement(eng.id);
      onDeleted(eng.id);
    } catch (e) {
      setErr(e.message);
      setDeleting(false);
    }
  };

  return (
    <div ref={ref} className="eng-del-wrap" onClick={e => e.stopPropagation()}>
      <button
        className="eng-del-btn"
        onClick={() => { setOpen(o => !o); setErr(''); }}
        title="Delete engagement"
      >
        ✕
      </button>
      {open && (
        <div className="eng-del-popover">
          <div className="eng-popover-title">Delete Engagement?</div>
          <div className="eng-del-popover-text">This cannot be undone.</div>
          {err && <div className="eng-popover-err">{err}</div>}
          <div className="eng-popover-actions">
            <button className="eng-btn-cancel" onClick={() => { setOpen(false); setErr(''); }}>Cancel</button>
            <button className="eng-del-confirm-btn" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PaginationBar({ page, pages, total, limit, onPage, onLimitChange }) {
  if (total === 0) return null;
  const from = (page - 1) * limit + 1;
  const to   = Math.min(page * limit, total);

  return (
    <div className="eng-pagination">
      <div className="eng-pagination-info">
        <span>
          {from}–{to} of <strong className="eng-pagination-total">{total}</strong> engagements
        </span>
        <div className="eng-pagination-size">
          <span>Show</span>
          <select
            value={limit}
            onChange={e => onLimitChange(Number(e.target.value))}
            className="eng-pagination-sel"
          >
            {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>per page</span>
        </div>
      </div>

      {pages > 1 && (
        <div className="eng-pagination-btns">
          <button className="eng-page-btn" disabled={page === 1} onClick={() => onPage(1)}>«</button>
          <button className="eng-page-btn" disabled={page === 1} onClick={() => onPage(page - 1)}>‹ Prev</button>

          {Array.from({ length: pages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === pages || Math.abs(p - page) <= 2)
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) =>
              p === '…'
                ? <span key={`e-${i}`} className="eng-pagination-ellipsis">…</span>
                : <button
                    key={p}
                    className={p === page ? 'eng-page-btn eng-page-btn--active' : 'eng-page-btn'}
                    onClick={() => onPage(p)}
                  >{p}</button>
            )
          }

          <button className="eng-page-btn" disabled={page === pages} onClick={() => onPage(page + 1)}>Next ›</button>
          <button className="eng-page-btn" disabled={page === pages} onClick={() => onPage(pages)}>»</button>
        </div>
      )}
    </div>
  );
}

function EngRow({ row, onClick, isMobile, userRole, onStatusSaved, onDeleted }) {
  const engLabel        = row.engagement_id || `EN-${String(row.id || '').slice(-8).toUpperCase()}`;
  const status          = row.status || 'synced';
  const company         = row.company_name || '—';
  const canChangeStatus = !TERMINAL_STATUSES.includes(status) || !!userRole?.isAdmin;
  const lastSync = row.latest_sync_at
    ? new Date(row.latest_sync_at).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false,
        timeZone: 'Asia/Kolkata',
      }).replace(',', '') + ' IST'
    : '—';

  if (isMobile) {
    return (
      <div onClick={() => onClick(row)} className="eng-mobile-card">
        <div className="eng-mobile-top">
          <div>
            <div className="eng-id-label">{engLabel}</div>
            {row.customer_pan && <div className="eng-pan-label">PAN: {row.customer_pan}</div>}
          </div>
          <div className="eng-mobile-status-wrap">
            <StatusBadge status={status} />
            {canChangeStatus && <InlineStatusChanger eng={row} userRole={userRole} onStatusSaved={onStatusSaved} />}
            <InlineDeleteButton eng={row} userRole={userRole} onDeleted={onDeleted} />
          </div>
        </div>
        <div className="eng-mobile-company">{company}</div>
        {row.customer_name && (
          <div className="eng-mobile-customer">{row.customer_name} · {row.customer_email}</div>
        )}
        <div className="eng-mobile-meta">
          {row.sync_count || 0} sync{row.sync_count !== 1 ? 's' : ''}
          {row.period_from && ` · ${row.period_from} – ${row.period_to}`}
        </div>
        <div className="eng-mobile-date">{lastSync}</div>
      </div>
    );
  }

  return (
    <tr className="eng-tr" onClick={() => onClick(row)}>
      <td className="eng-td">
        <div className="eng-id-label">{engLabel}</div>
        <div className="eng-created-date">
          {row.created_at
            ? new Date(row.created_at).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
              })
            : ''}
        </div>
      </td>
      <td className="eng-td">
        {row.customer_name
          ? <div>
              <div className="eng-cell-name">{row.customer_name}</div>
              <div className="eng-cell-email">{row.customer_email}</div>
              {row.customer_pan && <div className="eng-cell-pan">PAN: {row.customer_pan}</div>}
            </div>
          : <span className="eng-dim">—</span>}
      </td>
      <td className="eng-td eng-cell-company">{company}</td>
      <td className="eng-td eng-cell-center">{row.sync_count || 0}</td>
      <td className="eng-td eng-cell-period">
        {row.period_from && row.period_to ? `${row.period_from} – ${row.period_to}` : '—'}
      </td>
      <td className="eng-td eng-cell-date">{lastSync}</td>
      <td className="eng-td eng-td--stop" onClick={e => e.stopPropagation()}>
        <div className="eng-cell-status">
          <StatusBadge status={status} />
          {canChangeStatus && <InlineStatusChanger eng={row} userRole={userRole} onStatusSaved={onStatusSaved} />}
          <InlineDeleteButton eng={row} userRole={userRole} onDeleted={onDeleted} />
        </div>
      </td>
    </tr>
  );
}

const TNC_CONTENT = `
1. Client Authorization
You confirm that you have obtained explicit written consent from your client to access and sync their Tally ERP data on their behalf for the purpose of this audit engagement.

2. Data Responsibility
You acknowledge sole responsibility for ensuring the accuracy, completeness, and security of data synced through this engagement. AuditPrOS is a tool to facilitate the audit — the professional responsibility remains with you as the CA.

3. Confidentiality
All client financial data accessed through this sync shall be handled in strict professional confidence, used solely for this audit engagement, and not shared with any third party without the client's consent.

4. Professional Compliance
You confirm that syncing data on behalf of your client complies with the ICAI Code of Ethics, applicable data protection laws, and your engagement letter with the client.

5. Liability
You accept full professional liability for any issues arising from CA-managed data access or handling, including any inaccuracies in the synced data or unauthorized access to client systems.

By clicking "I Accept & Proceed", you acknowledge that you have read, understood, and agreed to all of the above terms.
`.trim();

function TnCModal({ onAccept, onCancel }) {
  const [canAccept, setCanAccept] = useState(false);
  const bodyRef = useRef(null);

  const handleScroll = () => {
    const el = bodyRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setCanAccept(true);
    }
  };

  return (
    <div className="eng-tnc-overlay" onClick={onCancel}>
      <div className="eng-tnc-modal" onClick={e => e.stopPropagation()}>
        <div className="eng-tnc-header">
          <div className="eng-tnc-title">Terms &amp; Conditions — CA-Managed Data Sync</div>
          <div className="eng-tnc-subtitle">Please read carefully before proceeding</div>
        </div>
        <div className="eng-tnc-body" ref={bodyRef} onScroll={handleScroll}>
          {TNC_CONTENT.split('\n\n').map((para, i) => {
            const [heading, ...rest] = para.split('\n');
            return (
              <div key={i} className="eng-tnc-section">
                <div className="eng-tnc-section-title">{heading}</div>
                {rest.length > 0 && <div className="eng-tnc-section-text">{rest.join(' ')}</div>}
              </div>
            );
          })}
          <div className="eng-tnc-scroll-hint">
            {canAccept ? null : '↓ Scroll to read all terms'}
          </div>
        </div>
        <div className="eng-tnc-footer">
          <button className="eng-btn-cancel" onClick={onCancel}>Cancel</button>
          <button
            className="eng-btn-accept"
            onClick={onAccept}
            disabled={!canAccept}
          >
            I Accept &amp; Proceed
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateEngagementModal({ onClose, onCreated }) {
  const [customers,   setCustomers]   = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const [lookupMode,    setLookupMode]    = useState('list');
  const [listTab,       setListTab]       = useState('managed');   // 'managed' | 'interactive'
  const [customerId,    setCustomerId]    = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPan,   setCustomerPan]   = useState('');

  // Default to 'ca' because the default list tab is Managed
  const [syncBy,  setSyncBy]  = useState('ca');
  const [showTnC, setShowTnC] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  useEffect(() => {
    api.customers()
      .then(c => setCustomers(Array.isArray(c) ? c : (c?.customers || c?.rows || [])))
      .catch(() => {})
      .finally(() => setLoadingData(false));
  }, []);

  // Only force-managed when in the list/managed path
  const isManaged = lookupMode === 'list' && listTab === 'managed';

  const managedCustomers     = customers.filter(c => c.engagement_mode === 'managed');
  const interactiveCustomers = customers.filter(c => c.engagement_mode !== 'managed');
  const listCustomers = listTab === 'managed' ? managedCustomers : interactiveCustomers;

  const hasCustomer = lookupMode === 'list' ? !!customerId
    : lookupMode === 'email' ? customerEmail.trim().length > 3
    : customerPan.trim().length >= 10;

  const switchListTab = (tab) => {
    setListTab(tab);
    setCustomerId('');
    setErr('');
    setSyncBy(tab === 'managed' ? 'ca' : 'customer');
  };

  const switchLookupMode = (m) => {
    setLookupMode(m);
    setCustomerId(''); setCustomerEmail(''); setCustomerPan(''); setErr('');
    // Restore managed/ca default when returning to list
    if (m === 'list') setSyncBy(listTab === 'managed' ? 'ca' : 'customer');
  };

  const doCreate = async (finalSyncBy) => {
    setSaving(true); setErr('');
    try {
      const body = { sync_responsibility: finalSyncBy };
      if (lookupMode === 'list')  body.customer_id    = customerId;
      if (lookupMode === 'email') body.customer_email = customerEmail.trim();
      if (lookupMode === 'pan')   body.customer_pan   = customerPan.trim().toUpperCase();

      const result = await api.createEngagement(body);
      onCreated(result);
    } catch (e) {
      setErr(e.message);
      setShowTnC(false);
    } finally {
      setSaving(false);
    }
  };

  const submit = () => {
    if (!hasCustomer) { setErr('Please identify the customer.'); return; }
    if (syncBy === 'ca') { setShowTnC(true); } else { doCreate('customer'); }
  };

  return (
  <>
    <div className="eng-modal-overlay" onClick={onClose}>
      <div className="eng-modal" onClick={e => e.stopPropagation()}>
        <div className="eng-modal-header">
          <div className="eng-modal-title">New Engagement</div>
          <button className="eng-modal-close" onClick={onClose}>✕</button>
        </div>

        {loadingData ? (
          <div className="eng-modal-loading"><Skeleton rows={3} /></div>
        ) : (
          <div className="eng-modal-body">

            {/* ── Customer lookup mode ── */}
            <label className="eng-modal-label">Customer</label>
            <div className="eng-lookup-tabs">
              {[['list','From list'],['email','By email'],['pan','By PAN']].map(([m, l]) => (
                <button
                  key={m}
                  className={`eng-lookup-tab${lookupMode === m ? ' eng-lookup-tab--active' : ''}`}
                  onClick={() => switchLookupMode(m)}
                >
                  {l}
                </button>
              ))}
            </div>

            {lookupMode === 'list' && (
              <>
                {/* ── Managed / Interactive sub-tabs ── */}
                <div className="eng-list-subtabs">
                  <button
                    className={`eng-list-subtab${listTab === 'managed' ? ' eng-list-subtab--active' : ''}`}
                    onClick={() => switchListTab('managed')}
                  >
                    Managed
                    {managedCustomers.length > 0 && (
                      <span className="eng-list-subtab-count">{managedCustomers.length}</span>
                    )}
                  </button>
                  <button
                    className={`eng-list-subtab${listTab === 'interactive' ? ' eng-list-subtab--active' : ''}`}
                    onClick={() => switchListTab('interactive')}
                  >
                    Interactive
                    {interactiveCustomers.length > 0 && (
                      <span className="eng-list-subtab-count">{interactiveCustomers.length}</span>
                    )}
                  </button>
                </div>

                <select
                  className="eng-modal-select"
                  value={customerId}
                  onChange={e => setCustomerId(e.target.value)}
                >
                  <option value="">
                    {listCustomers.length === 0
                      ? `No ${listTab} customers yet`
                      : `Select ${listTab} customer…`}
                  </option>
                  {listCustomers.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.business_name || c.name || c.email}
                      {c.pan_number ? ` — PAN: ${c.pan_number}` : ''}
                    </option>
                  ))}
                </select>
              </>
            )}

            {lookupMode === 'email' && (
              <input
                type="email"
                className="eng-modal-input"
                placeholder="customer@company.com"
                value={customerEmail}
                onChange={e => setCustomerEmail(e.target.value)}
              />
            )}

            {lookupMode === 'pan' && (
              <input
                type="text"
                className="eng-modal-input"
                placeholder="AAAPL1234C"
                maxLength={10}
                value={customerPan}
                onChange={e => setCustomerPan(e.target.value.toUpperCase())}
              />
            )}

            {/* ── Who syncs? ── */}
            <label className="eng-modal-label">Who will sync the data?</label>
            <div className="eng-sync-by-group">
              <button
                type="button"
                className={`eng-sync-by-opt${syncBy === 'customer' ? ' eng-sync-by-opt--active' : ''}${isManaged ? ' eng-sync-by-opt--disabled' : ''}`}
                onClick={() => { if (!isManaged) setSyncBy('customer'); }}
                disabled={isManaged}
                title={isManaged ? 'Managed customers are always synced by the CA' : undefined}
              >
                <div className="eng-sync-by-label">Customer will sync</div>
                <div className="eng-sync-by-desc">
                  {isManaged
                    ? 'Not available — managed customers are always synced by the CA.'
                    : 'An invitation email is sent to the customer to set up sync.'}
                </div>
              </button>
              <button
                type="button"
                className={`eng-sync-by-opt${syncBy === 'ca' ? ' eng-sync-by-opt--active' : ''}`}
                onClick={() => setSyncBy('ca')}
              >
                <div className="eng-sync-by-label">I (CA) will sync on their behalf</div>
                <div className="eng-sync-by-desc">You sync via the Tally connector. A privacy undertaking is required.</div>
              </button>
            </div>

            {syncBy === 'customer' && (
              <div className="eng-modal-hint">
                An email invitation will be sent to the customer to sync their Tally data.
              </div>
            )}

            {err && <div className="eng-modal-err">{err}</div>}

            <div className="eng-modal-actions">
              <button className="eng-btn-cancel" onClick={onClose}>Cancel</button>
              <button
                className="eng-btn-create"
                onClick={submit}
                disabled={saving || !hasCustomer}
              >
                {saving ? 'Creating…' : syncBy === 'ca' ? 'Continue → Sign Undertaking' : 'Create & Notify Customer'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>

    {showTnC && (
      <TnCModal
        onAccept={() => doCreate('ca')}
        onCancel={() => { setShowTnC(false); setSaving(false); }}
      />
    )}
  </>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const CUSTOMER_ENG_STATUS = {
  synced:           { label: 'Synced',          color: 'var(--green)',  bg: 'var(--green-bg)'       },
  in_progress:      { label: 'Under Review',    color: 'var(--accent)', bg: 'var(--blue-bg)'        },
  pending_customer: { label: 'Action Required', color: 'var(--amber)',  bg: 'var(--amber-bg)'       },
  pending_ca:       { label: 'Awaiting CA',     color: 'var(--purple)', bg: 'var(--purple-bg)'      },
  audit_completed:  { label: 'Completed',       color: 'var(--green)',  bg: 'var(--green-bg)'       },
  audit_abandoned:  { label: 'Abandoned',       color: 'var(--text2)',  bg: 'rgba(139,149,168,0.1)' },
};

function MyCustomerEngagements({ onOpenEngagement }) {
  const [engs,    setEngs]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.listMyEngagements()
      .then(rows => setEngs(Array.isArray(rows) ? rows : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || engs.length === 0) return null;

  return (
    <div className="eng-customer-section">
      <div className="eng-customer-section-title">
        My Engagements — as Customer
        <span className="eng-customer-section-count">{engs.length}</span>
      </div>
      <div className="eng-customer-list">
        {engs.map(eng => {
          const sc = CUSTOMER_ENG_STATUS[eng.status] || CUSTOMER_ENG_STATUS.in_progress;
          return (
            <div
              key={eng.id}
              className="eng-customer-card"
              onClick={() => onOpenEngagement({ ...eng, _isCustomerView: true })}
            >
              <div className="eng-customer-card-main">
                <div className="eng-customer-card-id">{eng.engagement_id}</div>
                <div className="eng-customer-card-company">{eng.company_name || '—'}</div>
                <div className="eng-customer-card-meta">
                  CA: {eng.ca_name || eng.ca_email}
                  {eng.customer_pan && <span> · PAN: {eng.customer_pan}</span>}
                  {eng.sync_count > 0 && <span> · {eng.sync_count} sync{eng.sync_count !== 1 ? 's' : ''}</span>}
                  {eng.latest_sync_at && <span> · Last sync {fmtDate(eng.latest_sync_at)}</span>}
                </div>
              </div>
              <div className="eng-customer-card-right">
                <span
                  className="eng-status-badge"
                  style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.color}33` }}
                >
                  {sc.label}
                </span>
                <span className="eng-customer-card-cta">View →</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function EngagementsDashboard({ userRole, onOpenEngagement }) {
  const [rows,       setRows]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [search,     setSearch]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page,       setPage]       = useState(1);
  const [pageSize,   setPageSize]   = useState(20);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [summary,    setSummary]    = useState({
    total: 0, in_progress: 0, pending_customer: 0, audit_completed: 0, pending_ca: 0,
  });
  const [showCreate, setShowCreate] = useState(false);

  const isMobile = useIsMobile();

  const load = useCallback(async (opts) => {
    setLoading(true); setError('');
    try {
      const data = await api.listEngagements({
        page: opts.page, limit: opts.pageSize,
        status: opts.filterStatus, search: opts.search,
      });
      setRows(data.rows ?? []);
      setTotal(data.total ?? 0);
      setTotalPages(data.pages ?? 1);
      if (data.summary) setSummary(data.summary);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load({ page: 1, pageSize, search: '', filterStatus: '' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      load({ page: 1, pageSize, search, filterStatus });
    }, 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPage(1);
    load({ page: 1, pageSize, search, filterStatus });
  }, [filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load({ page, pageSize, search, filterStatus });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = useCallback(() => {
    load({ page, pageSize, search, filterStatus });
  }, [page, pageSize, search, filterStatus, load]);

  const handleStatusSaved = useCallback((engId, newStatus) => {
    setRows(prev => prev.map(r => r.id === engId ? { ...r, status: newStatus } : r));
  }, []);

  const handleEngagementDeleted = useCallback((engId) => {
    setRows(prev => prev.filter(r => r.id !== engId));
    setTotal(prev => prev - 1);
  }, []);

  useOnSyncComplete(() => handleRefresh());

  return (
    <PageWrap>
      <PageHeader
        title="Engagements"
        sub="All audit engagements — click an engagement to view its sync history and audit data"
      />

      <div
        className="eng-summary-grid"
        style={{ gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)' }}
      >
        <SummaryCard label="Total"            count={summary.total}            color="var(--accent)" />
        <SummaryCard label="In Progress"      count={summary.in_progress}      color="#7C3AED" />
        <SummaryCard label="Pending Customer" count={summary.pending_customer} color="#0891B2" />
        <SummaryCard label="Audit Completed"  count={summary.audit_completed}  color="#1A6B4A" />
        <SummaryCard label="Pending CA"       count={summary.pending_ca}       color="#DC2626" />
      </div>

      <div className="eng-toolbar">
        <input
          type="text"
          placeholder="Search by customer, company or engagement ID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="eng-search"
        />
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="eng-select"
        >
          <option value="">All Statuses</option>
          {Object.entries(AUDIT_STATUS_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <button onClick={handleRefresh} className="eng-btn-refresh">↺ Refresh</button>
        <button onClick={() => setShowCreate(true)} className="eng-btn-new">+ New Engagement</button>
      </div>

      {error && <div className="eng-error-banner">{error}</div>}

      <Card style={{ padding: 0 }}>
        {loading ? (
          <div className="eng-loading"><Skeleton rows={6} /></div>
        ) : rows.length === 0 ? (
          <div className="eng-empty">
            <div className="eng-empty-icon">📁</div>
            <div>
              No engagements found
              {filterStatus ? ` with status "${AUDIT_STATUS_CONFIG[filterStatus]?.label}"` : ''}.
            </div>
            <div className="eng-empty-hint">
              Click <strong>+ New Engagement</strong> to create your first engagement.
            </div>
          </div>
        ) : isMobile ? (
          <div className="eng-mobile-list">
            {rows.map(r => (
              <EngRow
                key={r.id} row={r} onClick={onOpenEngagement}
                isMobile userRole={userRole} onStatusSaved={handleStatusSaved}
                onDeleted={handleEngagementDeleted}
              />
            ))}
          </div>
        ) : (
          <div className="eng-table-wrap">
            <table className="eng-table">
              <thead>
                <tr>
                  {['Engagement', 'Customer', 'Company', 'Syncs', 'Period', 'Last Sync', 'Status'].map(h => (
                    <th key={h} className="eng-th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <EngRow
                    key={r.id} row={r} onClick={onOpenEngagement}
                    userRole={userRole} onStatusSaved={handleStatusSaved}
                    onDeleted={handleEngagementDeleted}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <PaginationBar
          page={page} pages={totalPages} total={total} limit={pageSize}
          onPage={setPage} onLimitChange={n => { setPageSize(n); setPage(1); }}
        />
      </Card>

      {showCreate && (
        <CreateEngagementModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); handleRefresh(); }}
        />
      )}
    </PageWrap>
  );
}
